import { HttpClient, HttpContext, HttpContextToken, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { AuthenticationService } from '../../auth/authentication.service';
import { AjsResponse, wrapAjs } from '../ajs-response';
import { ImportJob, ImportJobMapping, JobType } from './import-jobs-api.types';

/**
 * ImportJobsApiService (Angular 18) — Import/Export Jobs API microservice.
 *
 * Replaces apps/b2b-ui/common/services/api/import/importJobsApi.service.js.
 *
 * Endpoint base hardcoded to /service/import/api/job (legacy provider's
 * setEndpoint() value from apps/b2b-ui/common/services/api/api.service.js
 * .config() block).
 *
 * Legacy quirks preserved:
 *   - `x-tenant-id` header set from the `user` cookie's `tenantId`. The legacy
 *     captures the cookie ONCE at service construction; we match that
 *     behavior for parity.
 *   - `getMappingsWithPagination` prefixes the `sort` value with `key_` when
 *     it matches `fieldTo` / `tableType` (legacy backend requirement).
 *   - `remove()` sets `ignoreConflict: true`. Legacy `conflictInterceptor`
 *     reads `response.config.ignoreConflict` to re-throw 409s without
 *     broadcasting a modal. Preserved via an `HttpContextToken<boolean>`
 *     (`IGNORE_CONFLICT`) so a future Angular 18 conflict interceptor can
 *     read `req.context.get(IGNORE_CONFLICT)` with identical semantics.
 */
const JOBS_BASE = '/service/import/api/job';

/**
 * HttpContext token mirroring the legacy `$http` config `ignoreConflict`
 * flag. Future Angular 18 interceptors should read this token to decide
 * whether to suppress conflict modal broadcasts on 409 responses.
 */
export const IGNORE_CONFLICT = new HttpContextToken<boolean>(() => false);

@Injectable({ providedIn: 'root' })
export class ImportJobsApiService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthenticationService);

  // Legacy reads the cookie ONCE at construction (provider's $get factory)
  // and reuses the captured tenantId on every subsequent request. Mirrored
  // here for behavioral parity.
  private readonly tenantId: string | undefined = this.readTenantId();

  private readTenantId(): string | undefined {
    const userCookie = this.auth.getCookie<{ tenantId?: string }>('user') ?? {};
    return userCookie.tenantId;
  }

  private get tenantHeaders(): HttpHeaders {
    return new HttpHeaders({ 'x-tenant-id': this.tenantId ?? '' });
  }

  list(
    page?: number | null,
    size?: number | null,
    sort?: string | null,
    type?: JobType | null,
  ): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(JOBS_BASE, {
        headers: this.tenantHeaders,
        params: {
          page: page ?? '',
          size: size ?? '',
          sort: sort ?? '',
          type: type ?? '',
        },
      }),
    );
  }

  getMappings(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.get<unknown>(`${JOBS_BASE}/${id}/mappings`));
  }

  getMappingsWithPagination(
    id: number | string,
    page?: number | null,
    size?: number | null,
    sort?: string | null,
  ): Promise<AjsResponse<unknown>> {
    let normalizedSort = sort ?? null;
    if (normalizedSort && /fieldTo|tableType/.test(normalizedSort)) {
      normalizedSort = `key_${normalizedSort}`;
    }
    return wrapAjs(
      this.http.get<unknown>(`${JOBS_BASE}/${id}/mappings/paging`, {
        headers: this.tenantHeaders,
        params: {
          page: page ?? '',
          size: size ?? '',
          sort: normalizedSort ?? '',
        },
      }),
    );
  }

  getTypes(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.get<unknown>(`${JOBS_BASE}/${id}/types`));
  }

  // `mappings` may be a pre-stringified JSON string, e.g. from the raw JSON
  // editor modal. Legacy $http always sent application/json for POST bodies
  // regardless of whether the body was a string or an object. HttpClient
  // infers Content-Type from the body's runtime type instead, so a string
  // body would otherwise be sent as text/plain and rejected by the backend.
  updateMappings(id: number | string, mappings: ImportJobMapping[] | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.post<unknown>(`${JOBS_BASE}/${id}/mappings`, mappings, {
        headers: this.tenantHeaders.set('Content-Type', 'application/json'),
      }),
    );
  }

  create(name: string, description: string, type: JobType): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.post<unknown>(JOBS_BASE, { name, description, jobType: type }, { headers: this.tenantHeaders }),
    );
  }

  update(job: ImportJob): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.put<unknown>(JOBS_BASE, job, { headers: this.tenantHeaders }));
  }

  get(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(`${JOBS_BASE}/${id}`, {
        headers: this.tenantHeaders,
      }),
    );
  }

  remove(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.delete<unknown>(`${JOBS_BASE}/${id}`, {
        headers: this.tenantHeaders,
        context: new HttpContext().set(IGNORE_CONFLICT, true),
      }),
    );
  }
}
